import { useEffect, useState } from "react";

import { Switch } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import Modal from "../../../layouts/Modal";
import useSingleLead from "../../../data/useSingleLead";
import { ILead, IUpdateQuoteDetailsRequest, LeadStatus } from "../../../models/Lead";
import classNames from "../../../utils/classNames";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";

const fromLead = (lead: ILead): IUpdateQuoteDetailsRequest => ({
  v5C: lead.v5C,
  keys: lead.keys ?? 0,
  accessToVehicle: lead.accessToVehicle,
  roadSidePayment: lead.roadSidePayment,
  fastPayment: lead.fastPayment,
  // The API sends a full timestamp; the date input only takes the day.
  preferredCollectionDate: lead.preferredCollectionDate?.slice(0, 10) ?? null,
});

function YesNoRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <Switch.Group as="div" className="flex items-center justify-between py-3">
      <Switch.Label as="span" className="text-sm font-medium text-gray-700" passive>
        {label}
      </Switch.Label>
      <Switch
        checked={checked}
        onChange={onChange}
        className={classNames(
          checked ? "bg-primary-600" : "bg-gray-200",
          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        )}
      >
        <span
          aria-hidden="true"
          className={classNames(
            checked ? "translate-x-5" : "translate-x-0",
            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
          )}
        />
      </Switch>
    </Switch.Group>
  );
}

/**
 * Corrects the customer's widget form answers. These are what an allocated yard is told, but they
 * only travel with the allocation — so editing a lead that has already gone asks first.
 */
export default function EditQuoteDetailsModal({
  lead,
  showPaymentOptions,
  open,
  setOpen,
}: {
  lead: ILead;
  /** Roadside and fast payment only apply to leads without a collection option. */
  showPaymentOptions: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { t } = useTranslation("leads");
  const { addNotification } = useNotification();
  const { updateQuoteDetails } = useSingleLead(lead.id, "EditQuoteDetailsModal");
  const [details, setDetails] = useState<IUpdateQuoteDetailsRequest>(() => fromLead(lead));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isAllocated = lead.status === LeadStatus.Allocated;

  // Start from the lead as it is now each time the modal opens, not from a cancelled edit.
  useEffect(() => {
    if (open) setDetails(fromLead(lead));
  }, [open, lead]);

  const set = <K extends keyof IUpdateQuoteDetailsRequest>(key: K, value: IUpdateQuoteDetailsRequest[K]) =>
    setDetails((prev) => ({ ...prev, [key]: value }));

  const save = () => {
    updateQuoteDetails.mutate(details, {
      onSuccess: () => {
        setConfirmOpen(false);
        setOpen(false);
        addNotification({ variant: "success", primaryText: t("Quote details updated") });
      },
      onError: (error: any) => {
        addNotification({ variant: "error", primaryText: error?.message ?? t("Unable to update quote details") });
      },
    });
  };

  const onSave = () => (isAllocated ? setConfirmOpen(true) : save());

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="max-w-lg">
        <h3 className="text-lg font-medium leading-6 text-gray-900">{t("Edit Quote Details")}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t("Correct what the customer entered on the online form. The quote keeps their original answers.")}
        </p>

        <div className="mt-4 divide-y divide-gray-200">
          <YesNoRow label={t("V5C")} checked={details.v5C} onChange={(v) => set("v5C", v)} />

          <div className="flex items-center justify-between py-3">
            <label htmlFor="quote-details-keys" className="text-sm font-medium text-gray-700">
              {t("Keys")}
            </label>
            <input
              id="quote-details-keys"
              type="number"
              min={0}
              step={1}
              value={details.keys}
              onChange={(e) => set("keys", Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="w-20 rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <YesNoRow label={t("Access To Vehicle")} checked={details.accessToVehicle} onChange={(v) => set("accessToVehicle", v)} />

          {showPaymentOptions && (
            <>
              <YesNoRow label={t("Road Side Payment")} checked={details.roadSidePayment} onChange={(v) => set("roadSidePayment", v)} />
              <YesNoRow label={t("Fast Payment")} checked={details.fastPayment} onChange={(v) => set("fastPayment", v)} />
            </>
          )}

          <div className="flex items-center justify-between gap-3 py-3">
            <label htmlFor="quote-details-date" className="text-sm font-medium text-gray-700">
              {t("Preferred Collection Date")}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="quote-details-date"
                type="date"
                value={details.preferredCollectionDate ?? ""}
                onChange={(e) => set("preferredCollectionDate", e.target.value || null)}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
              {details.preferredCollectionDate && (
                <button
                  type="button"
                  onClick={() => set("preferredCollectionDate", null)}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  {t("Clear")}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <ButtonInput
            label={t("Cancel")}
            onClick={() => setOpen(false)}
            disabled={updateQuoteDetails.isLoading}
            classes="border border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
          />
          <ButtonInput label={t("Save")} onClick={onSave} isLoading={updateQuoteDetails.isLoading} classes="" />
        </div>
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={t("This lead has been allocated")}
        message={t("The yard won't see this change. Allocate the lead again to send it to them.")}
        confirmButtonText={t("Save anyway")}
        onConfirm={save}
        isLoading={updateQuoteDetails.isLoading}
        Icon={<ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />}
        iconBg="bg-amber-100"
      />
    </>
  );
}
